import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { OUTLINE_EVENT, OUTLINE_REQUEST_EVENT } from './page-outline';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Globe, Info, Loader2, MousePointerClick, RefreshCw, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { IntegrationButtonConfig } from '../../../modules/vendor/settings/models/config.model';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { ApiMethod } from '../../../common/models/api-types.type';
import { routes } from '../../../assets/constants';

/** The page's layout width on a desktop. Shown scaled to the canvas, so the site lays out as it does for shoppers. */
const DESKTOP_WIDTH = 1280;
const MOBILE_WIDTH = 390;
const FRAME_HEIGHT = 640;
/** How long the picker may take to start once the page is shown. */
const READY_TIMEOUT_MS = 8000;
/** How long the live page gets to start its picker before the copy is shown instead. */
const LIVE_TIMEOUT_MS = 6000;
const URL_STORAGE_KEY = 'ardealy.bo.placer.url';
const PLACER_SCRIPT_PATH = '/placer/ardealy-placer.js';

/**
 * The placer's source and its CSP hash, fetched once.
 *
 * Inlined into the copy rather than loaded by it: a sandboxed frame has no
 * origin, and browsers refuse its requests to a private-network host (the
 * backoffice on localhost) outright. Inline, the script needs a hash both in
 * the copy's own policy and in the backoffice's (public/index.html), because a
 * srcdoc frame inherits its parent's policy too; index-csp.test.ts keeps that
 * second hash in step with the file.
 */
let placerPromise: Promise<{ source: string; hash: string }> | null = null;
const loadPlacer = () => {
    if (!placerPromise) {
        placerPromise = (async () => {
            const response = await fetch(PLACER_SCRIPT_PATH, { cache: 'no-cache' });
            if (!response.ok) throw new Error(`placer ${response.status}`);
            const source = await response.text();
            const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(source));
            const hash = `sha256-${btoa(String.fromCharCode(...Array.from(new Uint8Array(digest))))}`;
            return { source, hash };
        })().catch((error) => {
            placerPromise = null;
            throw error;
        });
    }
    return placerPromise;
};

const normalizeUrl = (raw: string) => {
    const url = raw.trim();
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

/** The live page, framed, with its SDK asked to run the placer. Needs a page that allows framing and a current SDK. */
const livePlacerUrl = (raw: string) => {
    try {
        const u = new URL(normalizeUrl(raw));
        u.searchParams.set('ardealy_pick', 'true');
        u.searchParams.set('ardealy_embed', '1');
        return u.toString();
    } catch {
        return null;
    }
};

/** The pop-up picker on the live page, for when the copy cannot be made. Needs the SDK on that page. */
const popupPickerUrl = (raw: string, device: 'web' | 'mobile') => {
    try {
        const u = new URL(normalizeUrl(raw));
        u.searchParams.set('ardealy_pick', 'true');
        if (device === 'mobile') u.searchParams.set('ardealy_device', 'mobile');
        return u.toString();
    } catch {
        return null;
    }
};

const plainText = (html: string) => {
    if (!html) return '';
    try {
        return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() || '';
    } catch {
        return html;
    }
};

/**
 * What the placer needs from the backoffice: the button's look, the saved
 * placement, and its words in the vendor's language. The same message goes to
 * the canvas frame and to a picker tab of its own.
 */
export const placerConfigMessage = (
    button: IntegrationButtonConfig | null,
    selector: string | undefined,
    position: string | undefined,
    t: (key: string, fallback: string) => string,
    /**
     * The widget's shape and words, when a widget is what is being placed.
     * Always sent — null included — so switching back to a button clears the
     * panel the placer was drawing.
     */
    panel: { aspectRatio?: string; label?: string; insertIndex?: number; minHeight?: number; panelCSS?: string } | null = null,
) => ({
    type: 'ardealy-placer-config',
    button: button ? { ...button, label: plainText(button.label || '') } : {},
    // Shape, position within the container and the vendor's panel CSS, so the
    // stand-in on the page is drawn the way the SDK will draw the panel.
    panel: panel ? {
        aspectRatio: panel.aspectRatio || '4:3',
        label: plainText(panel.label || ''),
        insertIndex: panel.insertIndex,
        minHeight: panel.minHeight,
        panelCSS: panel.panelCSS || '',
    } : null,
    selector: selector || '',
    position: position || '',
    labels: {
        before: t('INTEGRATION.PLACER.POS.BEFORE', 'Just before'),
        prepend: t('INTEGRATION.PLACER.POS.PREPEND', 'Inside, at the top'),
        append: t('INTEGRATION.PLACER.POS.APPEND', 'Inside, at the bottom'),
        after: t('INTEGRATION.PLACER.POS.AFTER', 'Just after'),
        overlay: t('INTEGRATION.PLACER.POS.OVERLAY', 'On top of it'),
        another: t('INTEGRATION.PLACER.ANOTHER', 'Pick another element'),
        hint: t('INTEGRATION.PLACER.TAB_HINT', 'Click an element for all spots, or the blue line above or below it to place the button there.'),
        cancel: t('INTEGRATION.PLACER.TAB_CANCEL', 'Cancel'),
        saved: t('INTEGRATION.PLACER.TAB_SAVED', 'Placement saved in Ardealy'),
        closing: t('INTEGRATION.PLACER.TAB_CLOSING', 'This tab closes in a moment…'),
    },
});

/**
 * Open the picker in a tab of its own, and answer it when it says it is ready.
 *
 * The tab cannot be sent anything before its page has loaded, so it announces
 * itself and gets the button's look, the placement and the labels in reply.
 * Its result arrives on the window like every other picker's.
 */
export const openPickerTab = (url: string, getConfig: () => ReturnType<typeof placerConfigMessage>) => {
    const tab = window.open(url, '_blank');
    if (!tab) return null;
    const onMessage = (e: MessageEvent) => {
        if (e.source !== tab) return;
        if (e.data?.type === 'ardealy-picker-ready') {
            try { tab.postMessage(getConfig(), '*'); } catch { /* the tab went away */ }
        }
        if (e.data?.type === 'ardealy-picker-result' || e.data?.type === 'ardealy-picker-cancel') {
            window.removeEventListener('message', onMessage);
        }
    };
    window.addEventListener('message', onMessage);
    // A tab closed without answering should not leave a listener behind forever.
    const watch = window.setInterval(() => {
        if (tab.closed) {
            window.clearInterval(watch);
            window.removeEventListener('message', onMessage);
        }
    }, 2000);
    return tab;
};

const readStoredUrl = () => {
    try { return window.localStorage.getItem(URL_STORAGE_KEY) || ''; } catch { return ''; }
};
const storeUrl = (url: string) => {
    try { window.localStorage.setItem(URL_STORAGE_KEY, url); } catch { /* per-viewer convenience only */ }
};

/**
 * The snapshot, ready for the frame: a policy that lets only the placer run,
 * and the placer itself.
 *
 * The API already removed the page's scripts; the policy is the second lock,
 * and the frame's sandbox (no same-origin) the third. The placer is inline and
 * allowed by its hash alone, so nothing else inline or loaded can run.
 */
export const buildSnapshotDocument = (html: string, placer: { source: string; hash: string }) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const csp = doc.createElement('meta');
    csp.httpEquiv = 'Content-Security-Policy';
    csp.content = [
        `script-src '${placer.hash}'`,
        "object-src 'none'",
        "frame-src 'none'",
        "worker-src 'none'",
        "form-action 'none'",
    ].join('; ');
    const base = doc.head.querySelector('base');
    doc.head.insertBefore(csp, base ? base.nextSibling : doc.head.firstChild);

    const script = doc.createElement('script');
    script.textContent = placer.source;
    doc.body.appendChild(script);
    return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
};

const errorMessage = async (error: any): Promise<string | null> => {
    try {
        if (error && typeof error.json === 'function') {
            const body = await error.json();
            const message = Array.isArray(body?.message) ? body.message[0] : body?.message;
            return typeof message === 'string' ? message : null;
        }
    } catch { /* fall through */ }
    return typeof error?.message === 'string' ? error.message : null;
};

interface LivePagePlacerProps {
    /** Connected domains, to suggest a page when nothing was loaded before. */
    domains: string[];
    /** A product page the vendor gave while setting up matching — the best first guess. */
    exampleUrl?: string;
    device: 'web' | 'mobile';
    /**
     * The button to draw on the page while it is being placed, or null when
     * what is being placed has no button — the in-gallery widget is picked the
     * same way, by pointing at the page, but there is nothing to preview
     * hovering over it.
     */
    button: IntegrationButtonConfig | null;
    /** Set when the widget is being placed, so the page previews a panel. */
    panel?: { aspectRatio?: string; label?: string; insertIndex?: number; minHeight?: number; panelCSS?: string } | null;
    selector?: string;
    position?: string;
}

type LoadState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'live'; src: string }
    | { kind: 'shown'; doc: string; finalUrl: string };

/**
 * The vendor's own product page, in the canvas, as the place to put the button.
 *
 * Shops refuse to be framed, so the page is not framed: the API fetches it and
 * returns its markup and styles without its scripts, and it is shown here in a
 * sandbox running only the placer (public/placer/ardealy-placer.js). The vendor
 * clicks an element, then a spot around it; the choice arrives as
 * `ardealy-picker-result` on the window, where the Button tab already listens
 * for the pop-up picker, so both routes save the same way. Nothing is stored.
 *
 * What the page's scripts would have drawn is missing from the copy, which is
 * said under it; the pop-up picker on the live page stays one click away.
 *
 * The live page comes first, when it can: framed with ?ardealy_embed=1, its own
 * SDK runs the same placer on the real, script-drawn page. That needs a site
 * that allows framing and a current SDK, so when the live page has not started
 * its picker after a few seconds the copy is shown instead. The vendor can also
 * choose either.
 */
export const LivePagePlacer = ({ domains, exampleUrl, device, button, panel = null, selector, position }: LivePagePlacerProps) => {
    const { t } = useTranslation();
    const { sendAuthGuardedRequest } = useAuthContext();
    const suggested = useMemo(
        () => readStoredUrl() || exampleUrl || (domains[0] ? `https://${domains[0]}` : ''),
        // Only the first render's suggestion: typing must not be overwritten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const [draft, setDraft] = useState(suggested);
    const [url, setUrl] = useState(suggested);
    const [reloadKey, setReloadKey] = useState(0);
    const [state, setState] = useState<LoadState>({ kind: 'idle' });
    const [ready, setReady] = useState(false);
    const [stalled, setStalled] = useState(false);
    const [found, setFound] = useState<boolean | null>(null);
    /*
     * The live page runs the SDK the vendor has installed, and the copy runs
     * ours. For a button that difference rarely shows; for the widget it
     * decides whether the thing works at all, because reading the gallery and
     * drawing a panel instead of a button are both new. A shop still serving
     * last month's script would show a dark pill where its widget is going to
     * be — the wrong surface, in the act meant to confirm the right one.
     *
     * So placing a widget starts on the copy. The vendor can still switch.
     */
    const [view, setView] = useState<'live' | 'copy'>(panel ? 'copy' : 'live');
    /** Whether the vendor chose the view; if not, a live page that does not start falls back to the copy. */
    const [viewChosen, setViewChosen] = useState(false);

    /*
     * The surface can change under a placer that is already mounted: switching
     * scope from the button to the widget swaps the props without this
     * component ever unmounting. An initial state would therefore be whichever
     * surface happened to be open first — which is why a widget kept being
     * placed on the live page, drawing the vendor's installed SDK's button
     * where its panel was going to go.
     */
    const placingWidget = !!panel;
    useEffect(() => {
        if (viewChosen) return;
        setView(placingWidget ? 'copy' : 'live');
    }, [placingWidget, viewChosen]);
    const [fellBack, setFellBack] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const [boxWidth, setBoxWidth] = useState(0);

    const frameWidth = device === 'mobile' ? MOBILE_WIDTH : DESKTOP_WIDTH;
    const scale = device === 'mobile' || !boxWidth ? 1 : Math.min(1, boxWidth / DESKTOP_WIDTH);

    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => setBoxWidth(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Show the live page or fetch the copy whenever the address, the view or a reload asks.
    useEffect(() => {
        const target = normalizeUrl(url);
        if (!target) { setState({ kind: 'idle' }); return; }
        let cancelled = false;
        setReady(false);
        setStalled(false);
        setFound(null);
        if (view === 'live') {
            const src = livePlacerUrl(target);
            setState(src ? { kind: 'live', src } : { kind: 'error', message: t('INTEGRATION.PLACER.LOAD_FAILED', 'That page could not be loaded.') });
            return;
        }
        setState({ kind: 'loading' });
        (async () => {
            try {
                const [result, placer] = await Promise.all([
                    sendAuthGuardedRequest(
                        ApiMethod.POST,
                        routes.integration.pageSnapshot,
                        { url: target },
                    ) as Promise<{ html: string; finalUrl: string }>,
                    loadPlacer(),
                ]);
                if (cancelled) return;
                setState({ kind: 'shown', doc: buildSnapshotDocument(result.html, placer), finalUrl: result.finalUrl });
            } catch (error) {
                if (cancelled) return;
                const message = await errorMessage(error);
                setState({ kind: 'error', message: message || t('INTEGRATION.PLACER.LOAD_FAILED', 'That page could not be loaded.') });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, reloadKey, view]);

    // Once shown, the picker should say it is ready quickly. A live page that
    // does not falls back to the copy, unless the vendor asked for the live page.
    useEffect(() => {
        if (ready || (state.kind !== 'shown' && state.kind !== 'live')) return;
        const live = state.kind === 'live';
        const timer = window.setTimeout(() => {
            if (live && !viewChosen) {
                setFellBack(true);
                setView('copy');
            } else {
                setStalled(true);
            }
        }, live ? LIVE_TIMEOUT_MS : READY_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [state, device, ready, viewChosen]);

    const configMessage = useCallback(
        () => placerConfigMessage(button, selector, position, t as any, panel),
        [button, panel, selector, position, t],
    );
    const configRef = useRef(configMessage);
    configRef.current = configMessage;

    const sendConfig = useCallback(() => {
        const target = iframeRef.current?.contentWindow;
        if (!target) return;
        // The copy's frame has an opaque origin and the live page may have
        // redirected, so there is no origin to name; nothing here is private.
        target.postMessage(configMessage(), '*');
    }, [configMessage]);

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
            if (e.data?.type === 'ardealy-picker-ready') {
                setReady(true);
                setStalled(false);
                sendConfig();
            }
            if (e.data?.type === 'ardealy-placer-found') setFound(!!e.data.found);
            // The placer's answer to "describe the page", passed on to whoever asked.
            if (e.data?.type === 'ardealy-placer-outline') {
                window.dispatchEvent(new CustomEvent(OUTLINE_EVENT, { detail: e.data }));
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [sendConfig]);

    // "Describe where it goes" asks for the page; this frame is where it is.
    useEffect(() => {
        const onRequest = (e: Event) => {
            const requestId = (e as CustomEvent).detail?.requestId;
            const target = iframeRef.current?.contentWindow;
            if (!target || !ready) {
                // Nothing to read yet: answer at once rather than let the ask time out.
                window.dispatchEvent(new CustomEvent(OUTLINE_EVENT, { detail: { requestId, nodes: [] } }));
                return;
            }
            target.postMessage({ type: 'ardealy-placer-outline-request', requestId }, '*');
        };
        window.addEventListener(OUTLINE_REQUEST_EVENT, onRequest);
        return () => window.removeEventListener(OUTLINE_REQUEST_EVENT, onRequest);
    }, [ready]);

    // Keep the button on the page in step with the design and the saved placement.
    useEffect(() => { if (ready) sendConfig(); }, [ready, sendConfig]);

    // A new device re-lays the page out, and the picker starts again with it.
    useEffect(() => { setReady(false); setStalled(false); }, [device]);

    const load = () => {
        const next = draft.trim();
        if (!next) return;
        storeUrl(next);
        // A new page gets its own chance at being shown live.
        if (!viewChosen && view !== 'live' && !panel) setView('live');
        setFellBack(false);
        if (next === url) setReloadKey((k) => k + 1);
        setUrl(next);
    };

    const chooseView = (next: 'live' | 'copy') => {
        setViewChosen(true);
        setFellBack(false);
        if (next === view) setReloadKey((k) => k + 1);
        setView(next);
    };

    const openInNewTab = () => {
        const target = popupPickerUrl(url || draft, device);
        if (target) openPickerTab(target, () => configRef.current());
    };

    return (
        <div className="flex flex-col gap-3">
            {/* Address bar */}
            <div className="flex items-center gap-1.5 rounded-lg border bg-background p-1.5 ps-3">
                <Globe className="size-4 shrink-0 text-muted-foreground" />
                <input
                    className="h-8 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    placeholder={t('INTEGRATION.PLACER.URL_PLACEHOLDER', 'https://yourshop.com/products/…') as string}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                    aria-label={t('INTEGRATION.PLACER.URL_LABEL', 'Product page address') as string}
                />
                <Button type="button" variant="outline" size="sm" onClick={load} disabled={!draft.trim() || state.kind === 'loading'}>
                    {t('INTEGRATION.PLACER.LOAD', 'Load')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setReloadKey((k) => k + 1)}
                    disabled={!url || state.kind === 'loading'}
                    aria-label={t('INTEGRATION.PLACER.RELOAD', 'Reload') as string}
                >
                    <RefreshCw className="size-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={openInNewTab}
                    disabled={!(url || draft).trim()}
                    aria-label={t('INTEGRATION.PLACER.OPEN_TAB', 'Pick on the live page instead') as string}
                    title={t('INTEGRATION.PLACER.OPEN_TAB', 'Pick on the live page instead') as string}
                >
                    <ExternalLink className="size-4" />
                </Button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                    <MousePointerClick className="size-3.5 shrink-0" />
                    {t('INTEGRATION.PLACER.HINT', 'Hover near the top or bottom of an element and click the blue line to put the button just before or after it, or click the element for every spot.')}
                </p>
                <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    className="bg-background"
                    value={view}
                    onValueChange={(v) => v && chooseView(v as 'live' | 'copy')}
                >
                    <ToggleGroupItem value="live">{t('INTEGRATION.PLACER.VIEW_LIVE', 'Live page')}</ToggleGroupItem>
                    <ToggleGroupItem value="copy">{t('INTEGRATION.PLACER.VIEW_COPY', 'Copy')}</ToggleGroupItem>
                </ToggleGroup>
            </div>

            {/* The page */}
            <div ref={boxRef} className="relative w-full overflow-hidden rounded-lg border bg-background" style={{ height: FRAME_HEIGHT }}>
                {(state.kind === 'shown' || state.kind === 'live') && (
                    <div
                        className="absolute top-0 h-full"
                        style={device === 'mobile'
                            ? { left: '50%', width: MOBILE_WIDTH, transform: 'translateX(-50%)' }
                            : { left: 0, width: frameWidth }}
                    >
                        {state.kind === 'live' ? (
                        <iframe
                            key={`live-${reloadKey}-${device}`}
                            ref={iframeRef}
                            src={state.src}
                            // The shop's own origin, not ours; no popups or navigating the backoffice.
                            sandbox="allow-scripts allow-same-origin"
                            title={t('INTEGRATION.PLACER.FRAME_TITLE', 'Your product page') as string}
                            className="block border-0 bg-white"
                            style={{
                                width: frameWidth,
                                height: FRAME_HEIGHT / scale,
                                transform: scale !== 1 ? `scale(${scale})` : undefined,
                                transformOrigin: 'top left',
                            }}
                        />
                        ) : (
                        <iframe
                            key={`copy-${reloadKey}-${device}`}
                            ref={iframeRef}
                            srcDoc={state.doc}
                            // Scripts, but an opaque origin: nothing in the page can reach the backoffice.
                            sandbox="allow-scripts"
                            referrerPolicy="no-referrer"
                            title={t('INTEGRATION.PLACER.FRAME_TITLE', 'Your product page') as string}
                            className="block border-0 bg-white"
                            style={{
                                width: frameWidth,
                                height: FRAME_HEIGHT / scale,
                                transform: scale !== 1 ? `scale(${scale})` : undefined,
                                transformOrigin: 'top left',
                            }}
                        />
                        )}
                    </div>
                )}

                {state.kind === 'live' && !ready && !stalled && (
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center p-3">
                        <span className="flex items-center gap-2 rounded-full border bg-background/95 px-3 py-1 text-xs text-muted-foreground shadow-sm">
                            <Loader2 className="size-3 animate-spin" />
                            {t('INTEGRATION.PLACER.TRYING_LIVE', 'Opening your live page…')}
                        </span>
                    </div>
                )}

                {state.kind === 'idle' && (
                    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                        <Globe className="size-6 text-muted-foreground" />
                        <p className="max-w-sm text-sm text-muted-foreground">
                            {t('INTEGRATION.PLACER.EMPTY', 'Enter the address of one of your product pages to load it here.')}
                        </p>
                    </div>
                )}

                {state.kind === 'loading' && (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="size-5 animate-spin" />
                        {t('INTEGRATION.PLACER.LOADING', 'Loading your page…')}
                    </div>
                )}

                {state.kind === 'error' && (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                        <TriangleAlert className="size-5 text-warning" />
                        <p className="max-w-md text-sm text-muted-foreground">{state.message}</p>
                        <Button type="button" variant="outline" size="sm" onClick={openInNewTab}>
                            <ExternalLink className="size-3.5" />
                            {t('INTEGRATION.PLACER.OPEN_TAB', 'Pick on the live page instead')}
                        </Button>
                    </div>
                )}

                {(state.kind === 'shown' || state.kind === 'live') && stalled && !ready && (
                    <div className="absolute inset-x-3 bottom-3 flex flex-wrap items-center gap-3 rounded-lg border bg-background/95 p-3 shadow-sm backdrop-blur">
                        <TriangleAlert className="size-4 shrink-0 text-warning" />
                        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                            {state.kind === 'live'
                                ? t('INTEGRATION.PLACER.STALLED_LIVE', 'The picker didn’t start on your live page. It may not allow being shown here, or it runs an older SDK. Try the copy.')
                                : t('INTEGRATION.PLACER.STALLED', 'The picker didn’t start on this page. Reload, or pick on the live page instead.')}
                        </p>
                        <Button type="button" variant="outline" size="sm" onClick={openInNewTab}>
                            <ExternalLink className="size-3.5" />
                            {t('INTEGRATION.PLACER.OPEN_TAB', 'Pick on the live page instead')}
                        </Button>
                    </div>
                )}
            </div>

            {state.kind === 'shown' && fellBack && (
                <p className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Info className="mt-px size-3.5 shrink-0" />
                    {t('INTEGRATION.PLACER.FELL_BACK', 'Your live page couldn’t be shown here, so this is a copy of it.')}
                </p>
            )}

            {state.kind === 'shown' && (
                <p className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Info className="mt-px size-3.5 shrink-0" />
                    {t('INTEGRATION.PLACER.COPY_NOTE', 'A copy of your page without its scripts, so parts that scripts draw — sliders, reviews, some menus — may be missing or look different. The button goes on the real page.')}
                </p>
            )}

            {ready && selector && found === false && (
                <p className="text-xs text-muted-foreground">
                    {t('INTEGRATION.PLACER.NOT_FOUND', 'The saved spot isn’t on this page. Pick one here, or load another product page.')}
                </p>
            )}
        </div>
    );
};
